/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import set from 'lodash/set';
import { NumberField } from 'sarsaparilla';
import GroupMemberItem from './GroupMemberItem';

const propTypes = {
    validation: PropTypes.shape().isRequired,
    reservation: PropTypes.shape().isRequired,
    pActions: PropTypes.shape().isRequired,
    permit: PropTypes.shape().isRequired,
    vIndex: PropTypes.number.isRequired,
    addToInputRefs: PropTypes.func.isRequired,
    displayValidationError: PropTypes.bool,
    removeInputRefs: PropTypes.func.isRequired,
};

const GROUP_MEMBER_PROP_PATH = 'group_members';

export default function GroupMemberListHOC({ hasBirthdate } = {}) {
    function GroupMemberList({
        reservation,
        permit,
        validation,
        pActions,
        addToInputRefs,
        removeInputRefs,
        vIndex,
    }) {
        const getGroupSizeProps = () => {
            const rules = (permit?.rules || [])
                .filter(
                    (rule) =>
                        rule.item_id ===
                        `${reservation.permit_id}__${reservation.division_id}`
                )
                .reduce((acc, rule) => {
                    acc[rule.name] = rule.value;
                    return acc;
                }, {});

            const maxGroupSize = get(rules, 'MaxGroupSize', 1);
            const minGroupSize = get(rules, 'MinGroupSize', 1);
            const groupMembers = get(reservation, GROUP_MEMBER_PROP_PATH, []);
            const memberCount = groupMembers.length;
            return { maxGroupSize, minGroupSize, memberCount, groupMembers };
        };

        const { maxGroupSize, minGroupSize, memberCount, groupMembers } =
            getGroupSizeProps();

        const addMember = (numMembersToAdd) => {
            if (memberCount + numMembersToAdd > maxGroupSize) return [...groupMembers];

            const newMembers = new Array(numMembersToAdd).fill({
                birth_date: '',
                first_name: '',
                last_name: '',
                middle_name: '',
            });

            return [...groupMembers, ...newMembers];
        };

        const handleGroupSizeChange = (value) => {
            let newMemberCount = value;

            if (newMemberCount > maxGroupSize) {
                newMemberCount = maxGroupSize;
            } else if (newMemberCount < minGroupSize) {
                newMemberCount = minGroupSize;
            }
            if (newMemberCount <= 1) newMemberCount = 1;

            if (newMemberCount === memberCount) return;

            const newGroupMembers =
                newMemberCount > memberCount
                    ? addMember(newMemberCount - memberCount)
                    : groupMembers.slice(0, newMemberCount);

            pActions.updatePendingReservation(
                reservation.id,
                GROUP_MEMBER_PROP_PATH,
                newGroupMembers
            );
        };

        const removeMember = (memberId, refIds) => {
            refIds.forEach((refId) => removeInputRefs(refId));
            const newGroupMembers = [...groupMembers];
            newGroupMembers.splice(memberId, 1);
            pActions.updatePendingReservation(
                reservation.id,
                GROUP_MEMBER_PROP_PATH,
                newGroupMembers
            );
        };

        const handleMemberInfoChange = (index, field, input) => {
            const rec = groupMembers[index];
            set(rec, field, input);
            pActions.updatePendingReservation(
                reservation.id,
                GROUP_MEMBER_PROP_PATH,
                groupMembers
            );
        };

        const renderMembers = () => {
            return groupMembers.map((member, index) => {
                const key = `member_indx_${index}`;
                return (
                    <GroupMemberItem
                        key={key}
                        member={member}
                        index={index}
                        permit={permit}
                        addToInputRefs={addToInputRefs}
                        vIndex={vIndex}
                        reservation={reservation}
                        handleMemberInfoChange={handleMemberInfoChange}
                        validation={validation}
                        focusOnNextAdd={false}
                        hasBirthdate={hasBirthdate}
                        removeMember={removeMember}
                    />
                );
            });
        };

        return (
            <div className="permit-group-member-wrap">
                <div>
                    <h2>Group Members</h2>
                    <p className="permit-group-header-text">
                        Add everyone that will be going with you. No changes or transfers
                        will be allowed once a pass is purchased.
                    </p>
                </div>
                <div>
                    <NumberField
                        id="total-group-size-input"
                        visualLabel="Total Group Size:"
                        units="group-size"
                        min={minGroupSize}
                        max={maxGroupSize}
                        onChange={handleGroupSizeChange}
                        value={groupMembers.length}
                    />
                </div>
                <div className="mt-1">
                    <div className="per-group-member-list">{renderMembers()}</div>
                </div>
            </div>
        );
    }

    GroupMemberList.propTypes = propTypes;

    return GroupMemberList;
}
